/**
 * useApplicationGroups Hook
 * Custom hook for managing Application Group dashboard state
 *
 * @version 2025.1.0
 * @description Hook para gerenciamento de estado do dashboard de Grupo de Aplicação
 */

import { useState, useCallback, useEffect } from 'react';
import { applicationGroupService } from '../../../services/applicationGroupService';
import { ConsolidatedData } from '../../../types/applicationGroup';

// ============================================================================
// TYPES
// ============================================================================

interface UseApplicationGroupsState {
  // Consolidated data state
  consolidated: ConsolidatedData | null;
  selectedCompanyId: number | null;

  // Loading states
  loading: boolean;

  // Error states
  error: string | null;
}

interface UseApplicationGroupsReturn extends UseApplicationGroupsState {
  // Consolidated methods
  loadConsolidatedData: (companyId: number) => Promise<void>;
  setSelectedCompanyId: (companyId: number | null) => void;
  refresh: () => Promise<void>;

  // Clear states
  clearError: () => void;
  clearAll: () => void;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: UseApplicationGroupsState = {
  consolidated: null,
  selectedCompanyId: null,
  loading: false,
  error: null,
};

// ============================================================================
// HOOK
// ============================================================================

export function useApplicationGroups(): UseApplicationGroupsReturn {
  const [state, setState] = useState<UseApplicationGroupsState>(initialState);

  /**
   * Load consolidated data for a company
   */
  const loadConsolidatedData = useCallback(
    async (companyId: number): Promise<void> => {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        selectedCompanyId: companyId,
      }));

      try {
        const data = await applicationGroupService.getConsolidatedByCompany(companyId);

        setState((prev) => ({
          ...prev,
          consolidated: data,
          loading: false,
        }));
      } catch (err: unknown) {
        const error = err as { message?: string };
        setState((prev) => ({
          ...prev,
          error: error.message || 'Erro ao carregar dados consolidados',
          loading: false,
        }));
      }
    },
    []
  );

  /**
   * Set selected company ID
   */
  const setSelectedCompanyId = useCallback((companyId: number | null): void => {
    setState((prev) => ({
      ...prev,
      selectedCompanyId: companyId,
      consolidated: companyId === null ? null : prev.consolidated,
    }));
  }, []);

  /**
   * Refresh current data
   */
  const refresh = useCallback(async (): Promise<void> => {
    if (state.selectedCompanyId !== null) {
      await loadConsolidatedData(state.selectedCompanyId);
    }
  }, [state.selectedCompanyId, loadConsolidatedData]);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * Clear all state
   */
  const clearAll = useCallback((): void => {
    setState(initialState);
  }, []);

  /**
   * Load consolidated data when company changes
   */
  useEffect(() => {
    if (state.selectedCompanyId !== null) {
      loadConsolidatedData(state.selectedCompanyId);
    }
  }, [state.selectedCompanyId, loadConsolidatedData]);

  return {
    ...state,
    loadConsolidatedData,
    setSelectedCompanyId,
    refresh,
    clearError,
    clearAll,
  };
}

export default useApplicationGroups;
